// External dependencies
import * as React from "react";
import { Link } from "@tanstack/react-router";

// UI Components
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import { AppLogo } from "@/components/shared/app-logo";

// Navigation Components
import { AppNavMain } from "./nav-main";
import { AppNavUser } from "./nav-user";

// Utilities and Hooks
import { useAuth } from "@/providers/auth.provider";
import { cn } from "@/lib/utils";

/**
 * AppSidebar - Main navigation sidebar component
 * Provides collapsible navigation with header, main content, and user footer
 * Automatically uses Sheet component for mobile devices
 */
export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user } = useAuth();
  const { open } = useSidebar();

  return (
    <Sidebar 
      collapsible="icon" 
      className="border-r"
      {...props}
    >
      <SidebarHeader>
        <div className={cn(
          "flex w-full items-center transition-all",
          open ? "justify-start px-2 py-2" : "justify-center py-2"
        )}>
          <Link to="/dashboard" className="flex items-center gap-2">
            {open ? (
              <AppLogo showText={false} />
            ) : (
              <img 
                src="/favicon.png" 
                alt="Clientix" 
                className="h-6 w-6"
              />
            )}
          </Link>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <AppNavMain />
      </SidebarContent>
      {user && (
        <SidebarFooter>
          <AppNavUser user={user} />
        </SidebarFooter>
      )}
      <SidebarRail />
    </Sidebar>
  );
}